import {
  Component,
  Input,
  Output,
  ViewChild,
  EventEmitter,
  ElementRef,
  TemplateRef 
} from '@angular/core';
import { TableColumn } from '../../../models/table-column.model';
import { DxDataGridComponent } from 'devextreme-angular';
import { Column } from 'devextreme/ui/data_grid';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import { saveAs } from 'file-saver-es';
import CustomStore from 'devextreme/data/custom_store';
import { PageStateService } from 'src/app/services/page-state.service';
import { TranslateService } from '@ngx-translate/core';
import DataSource from 'devextreme/data/data_source';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  standalone: false
})
export class TableComponent {
  @ViewChild('searchInputBox') set searchInputBoxContent(
    content: ElementRef<HTMLElement>
  ) {
    if (content) {
      this.searchBoxInput = content;
    }
  }
  searchBoxInput!: ElementRef<HTMLElement>;

  @ViewChild(DxDataGridComponent, { static: false })
  dataGrid!: DxDataGridComponent;

  @Input()
  data: any[] | CustomStore = [];

  @Input()
  columns: TableColumn[] = [];

  @Input()
  showExport: boolean = false;

  @Input()
  exportName!: string;

  @Input()
  showGrouping: boolean = false;

  @Input()
  showSelection: boolean = false;

  @Input()
  selectionMode: string = 'multiple';

  @Input()
  showSearch: boolean = false;

  @Input()
  searchLabel: string = '';

  @Input()
  searchPlacehoder: string = '';

  @Input()
  showActionButton: boolean = false;

  @Input()
  actionButtonIcon: string = '';

  @Input()
  actionButtonText: string = '';

  @Input()
  allowDataEditing: boolean = false;

  @Input()
  pagingMode: 'client' | 'server' = 'client';

  @Input()
  pageSize: number = 20;

  @Input()
  allowedPageSizes: Array<number> = [10, 20, 40];

  @Input()
  showPageSizeSelector: boolean = true;

  @Input()
  showPagination: boolean = true;
  
  @Input()
  showRowLines: boolean = true;

  @Input() 
  disableSorting: boolean = false;

  @Input() 
  disableFiltering: boolean = false;

  @Input() 
  showFilterPanel: boolean = false;

  @Input() 
  stateKey?: string;

  @Input() rowStyleCallback?: (row: any) => string | null;

  @Input() 
  templates: Record<string, TemplateRef<any>> = {};

  @Output()
  actionFunction = new EventEmitter();

  @Output()
  onRowClick = new EventEmitter();

  @Output()
  onRowUpdate = new EventEmitter();

  @Output()
  onRowUpdateStarted = new EventEmitter();

  @Output()
  selectHandler = new EventEmitter();

  @Input() 
  showRowDetails: boolean = false;

  @Input() 
  rowHasDetails?: (rowData: any) => boolean;
  
  @Input() 
  rowDetailTemplate?: TemplateRef<any>;

  @Input()
  getAdditionalRows?: (row: any) => any[];

  @Input()
  mapAdditionalRow?: (item: any, row: any) => any[];

  groupingValues: any;
  editingRowKey: any = null;

  searchValue = '';
  selectedGrouping: string | null = null;

  readonly defaultDescriptionLines = 3;

  private readonly filterOperationTranslations: Record<string, string> = {
    '=': 'COMMON.FILTER.EQUALS',
    '<>': 'COMMON.FILTER.NOT-EQUALS',
    'contains': 'COMMON.FILTER.CONTAINS',
    'notcontains': 'COMMON.FILTER.NOT-CONTAINS',
    'startswith': 'COMMON.FILTER.STARTS-WITH',
    'endswith': 'COMMON.FILTER.ENDS-WITH',
    'anyof': 'COMMON.FILTER.CONTAINS'
  };


  get isServerSide(): boolean {
    return this.pagingMode === 'server' &&
      (this.data instanceof CustomStore || this.data instanceof DataSource);
  }

  constructor(
    private pageStateService: PageStateService,
    private translateService: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.groupingValues = [
      {
        value: null,
        text: `Default`,
      },
    ];

    this.columns.map((column) => {
      if (column.dataField !== 'this' && column.caption) {
        this.groupingValues.push({
          value: column.dataField,
          text: column.caption,
        });
      }
    });
  }

  toggleGroupColumn(e: any) {
    this.dataGrid.instance.clearGrouping();

    if (e.value) {
      this.selectedGrouping = e.value;
      this.dataGrid.instance.columnOption(e.value, 'groupIndex', 0);
    }
  }

  calculateCellValueDefault(value: any) {
    const column = this as any;
    return column.defaultCalculateCellValue(value);
  }

  calculateFilterExpressionDefault(this: Column) {
    return this.defaultCalculateFilterExpression!.apply(
      this,
      arguments as unknown as [
        filterValue: any,
        selectedFilterOperation: string | null,
        target: string
      ]
    );
  }

  onTableSearch(event: any): void {
    this.searchValue = event.value;
    this.dataGrid.instance.searchByText(event.value);
    setTimeout(() => {
      // @ts-ignore
      this.searchBoxInput!.instance!.focus();
    }, 100);
  }

  onRowClickHandler(event: any) {
    if (this.onRowClick) {
      this.onRowClick.emit(event.data.id);
    }
  }

  onRowEditingStartHandler(e: any) {
    this.editingRowKey = e.key;
    if (this.onRowUpdateStarted) {
      this.onRowUpdateStarted.emit(this.editingRowKey);
    }
  }

  onRowUpdateHandler(event: any) {
    if (this.onRowUpdate) {
      this.onRowUpdate.emit(event);
    }
  }

  onRowUpdateCancelledHandler() {
    this.editingRowKey = null;
    if (this.onRowUpdateStarted) {
      this.onRowUpdateStarted.emit(null);
    }
  }

  onRowUpdateSavedHandler() {
    this.editingRowKey = null;
    if (this.onRowUpdateStarted) {
      this.onRowUpdateStarted.emit(null);
    }
  }

  onRowSelected(e: any) {
    this.selectHandler.emit(e.selectedRowKeys);
  }

  customizeBulletTooltip({ originalValue }: Record<string, string>) {
    return { text: `${parseInt(originalValue)}%` };
  }

  onRowPrepared(e: any) {
    if (e.rowType === 'data') {
      if (this.rowStyleCallback) {
        const cls = this.rowStyleCallback(e.data);
        if (cls) e.rowElement.classList.add(cls);
      }

      if (this.showRowDetails && this.rowHasDetails && !this.rowHasDetails(e.data)) {
        const arrow = e.rowElement.querySelector('.dx-datagrid-expand') as HTMLElement;
        if (arrow) arrow.style.visibility = 'hidden';
      }
    }
  }

  getBulletColor(value: number) {
    if (value >= 0 && value < 34) {
      return '#f53d3dcc';
    } else if (value >= 34 && value < 67) {
      return '#e8b34acc';
    } else if (value >= 67 && value < 100) {
      return '#aed959cc';
    } else if (value >= 100) {
      return '#66cc66cc';
    }
    return '#66cc66cc';
  }

  onExporting(e: any) {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet(this.exportName);

    const columns = this.dataGrid.instance.getVisibleColumns();
    const hiddenColumns: any[] = [];

    columns.forEach((col: any) => {
      if (col.dataType === 'action' || col.exportable === false) {
        this.dataGrid.instance.columnOption(col.index, 'visible', false);
        hiddenColumns.push(col.index);
      }
    });

    exportDataGrid({
      component: this.dataGrid.instance,
      worksheet,
      autoFilterEnabled: true,
    }).then(() => {
      hiddenColumns.forEach((index) => {
        this.dataGrid.instance.columnOption(index, 'visible', true);
      });

      if (this.getAdditionalRows) {
        const data = this.dataGrid.instance.getDataSource().items();

        let rowOffset = 1;

        data.forEach((row: any) => {
          const additional = this.getAdditionalRows!(row) || [];

          rowOffset++;

          additional.forEach((item: any) => {
            const values = this.mapAdditionalRow
              ? this.mapAdditionalRow(item, row)
              : [item?.name ?? ''];

            worksheet.insertRow(rowOffset + 1, values);

            rowOffset++;
          });
        });
      }

      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer], { type: 'application/octet-stream' }),
          `${this.exportName.toLowerCase().replace(/ /g, '-')}.xlsx`
        );
      });
    });
  }

  saveGridState = (state: any): Promise<void> => {
    if (this.stateKey) {
      state.searchText = this.searchValue;
      state.selectedGrouping = this.selectedGrouping;
      this.pageStateService.save(this.stateKey, state);
    }

    return Promise.resolve();
  };

  loadGridState = (): Promise<any> => {
    if (!this.stateKey) return Promise.resolve({});

    const state = 
      this.pageStateService.load<any>(this.stateKey);
    if (!state) return Promise.resolve({});
    
    this.searchValue = state.searchText ?? '';
    this.selectedGrouping = state.selectedGrouping ?? null;
  
    return Promise.resolve(state);
  };

  onContentReady(e: any) {
    const gridElement = e.element;
    const filterPanel = gridElement.querySelector(".dx-datagrid-filter-panel");

    const hasFilter = e.component.option("filterValue");
    if (filterPanel) {
      filterPanel.style.display = hasFilter ? "" : "none";
    }
  }

  customizeFilterPanelText = (info: any): string => {
    const filterText = this.formatFilter(info.filterValue);

    return filterText
      ? `${this.translateService.instant('COMMON.ACTIVE-FILTERS')}: ${filterText}`
      : '';
  };

  private formatFilter(filter: any): string {
    if (!Array.isArray(filter)) {
      return '';
    }

    // Nested expression
    if (Array.isArray(filter[0])) {
      return `${this.formatFilter(filter[0])} ${this.translateLogicalOperator(filter[1])} ${this.formatFilter(filter[2])}`;
    }

    const [field, operation, value] = filter;

    const operationText = this.translateService.instant(
      this.filterOperationTranslations[operation] ?? operation
    );

    const formattedValue = Array.isArray(value)
      ? `(${value.join(', ')})`
      : `'${value}'`;

    return `[${field}] ${operationText} ${formattedValue}`;
  }

  private translateLogicalOperator(operator: string): string {
    switch (operator) {
      case 'and':
        return this.translateService.instant('COMMON.AND');
      case 'or':
        return this.translateService.instant('COMMON.OR');
      default:
        return operator;
    }
  }
}